import React, { useState } from 'react';
import { TextField, Chip, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getUniqueItems } from 'src/common/config/FunctionHelperPartTwo';
import { orbitAiStepCSS } from 'src/content/onBoarding/OrbitAiStepCSS';

interface MultiChipTextFieldProps {
  values: string[];
  setValues: React.Dispatch<React.SetStateAction<string[]>> | ((vals: string[]) => void);
  maxLimit?: number;
  placeholder?: string;
  maxLength?: number;
  error?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}

export const MultiChipTextField: React.FC<MultiChipTextFieldProps> = ({
  values,
  setValues,
  maxLimit = 4,
  placeholder = 'Enter values (comma-separated)',
  maxLength = 50,
  error = false,
  autoFocus = false,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [internalError, setInternalError] = useState<{ open: boolean; message: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Step 1: Normalize input — convert newlines (\n, \r) to commas
    const rawValue = e.target.value.replace(/[\n\r]+/g, ',');

    // Step 2: Remove leading spaces for cleaner typing
    const value = rawValue.trimStart();

    // Step 3: Update input field state (used as controlled input)
    setInputValue(value);

    // Step 4: If already at max allowed items and user is still typing, show error
    if (values?.length >= maxLimit && value.length > 0) {
      setInternalError({
        open: true,
        message: `Only ${maxLimit} items allowed`
      });
      return;
    }

    // Check single input length
    if (!value?.includes(',') && value?.length > maxLength) {
      setInternalError({
        open: true,
        message: `Value cannot exceed ${maxLength} characters`
      });
    }

    // Step 5: Check if the current value contains commas (indicating new entry)
    if (value?.includes(',')) {
      // Split the string into parts using commas
      const parts = value.split(',');

      // Keep the last part as the residual input (likely unfinished typing)
      const lastPart = parts.pop()?.trim() ?? '';

      // Clean each item, remove empties
      const newItems = parts
        ?.map((kw) => kw?.trim())
        ?.filter((kw) => kw?.length > 0);

      if (newItems?.some((kw) => kw?.length > maxLength)) {
        setInternalError({
          open: true,
          message: `Value cannot exceed ${maxLength} characters`
        });
        return;
      }

      // Combine new items with already existing ones
      const combined = [...values, ...newItems];

      // Ensure uniqueness and respect max limit
      const uniqueItems = getUniqueItems(combined).slice(0, maxLimit);

      // Step 6: If too many items, trim and show error
      if (uniqueItems.length > maxLimit) {
        setValues(uniqueItems);
        setInputValue('');
        setInternalError({
          open: true,
          message: `Only ${maxLimit} items allowed`
        });
        return;
      }

      // Step 7: Update state with accepted values
      setValues(uniqueItems);

      // Step 8: Set the leftover input in the field (e.g., unfinished item)
      setInputValue(lastPart);

      // Step 9: Reset any existing error
      if (lastPart?.length > maxLength) {
        setInternalError({
          open: true,
          message: `Value cannot exceed ${maxLength} characters`
        });
      } else {
        setInternalError({ open: false, message: '' });
      }
    } else if (value?.trim() === '') {
      // Step 10: If field is cleared, reset error state
      setInternalError({ open: false, message: '' });
    } else if (value?.length <= maxLength) {
      setInternalError({ open: false, message: '' });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<any>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent newline
      if (inputValue?.trim()) {
        // Reuse the comma logic by appending a comma
        handleInputChange({
          target: { value: inputValue + ',' }
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }

    // Delete last chip on Backspace when input is empty
    if (
      e.key === 'Backspace' &&
      inputValue?.trim() === '' &&
      values?.length > 0
    ) {
      e.preventDefault(); // prevent text flicker
      setValues(values.slice(0, -1)); // remove last item
    }
  };

  const handleBlur = () => {
    if (inputValue?.trim()) {
      handleInputChange({
        target: { value: inputValue + ',' }
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleDelete = (itemToRemove: string) => {
    setValues(values.filter((item) => item !== itemToRemove));
  };

  return (
    <div>
      <TextField
        variant="outlined"
        multiline
        placeholder={values?.length < maxLimit ? placeholder : ''}
        fullWidth
        value={inputValue}
        error={error || internalError?.open}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        InputProps={{
          startAdornment: (
            <Box sx={orbitAiStepCSS?.inputBoxStart}>
              {values?.map((val, index) => (
                <Chip
                  key={index}
                  label={val}
                  onDelete={disabled ? undefined : () => handleDelete(val)}
                  sx={orbitAiStepCSS?.inputChip}
                  disabled={disabled}
                  deleteIcon={
                    <CloseIcon
                      sx={{
                        color: 'var(--color-text-dark) !important',
                        fontSize: '18px !important'
                      }}
                    />
                  }
                />
              ))}
            </Box>
          )
        }}
        sx={orbitAiStepCSS?.textFieldInput}
      />
      {internalError?.open && (
        <p className="text-[#D32F2F] text-xs mt-1 font-medium ml-1">
          {internalError?.message}
        </p>
      )}
    </div>
  );
};
